import {alpha, Box, Paper, Stack, SxProps, Theme} from '@mui/material'
import SecuritySearch from '../../../components/securitySearch/SecuritySearch'
import {useSelector} from 'react-redux'
import * as indicatorProto from '../../../proto/generated/indicator'
import React, {memo, useCallback, useContext, useMemo, useRef, useState} from 'react'
import {ChartViewKey, OptionChainViewKey, ViewType} from '../../../assets/constants'
import LtpView from '../ltpView/ltpView'
import {useResizeDetector} from 'react-resize-detector'
import MarketStatus from '../marketStatus'
import {selectActiveLayoutId} from '../../../selectors/home'
import {AppContext} from '../../../App'
import {selectActiveChartId, selectChartConfig, selectChartFocused, selectChartReplayMode} from '../../../selectors/chart'
import {selectActiveWidget} from '../../../selectors/widgetDashboard'
import {WidgetTypes} from '../../../components/widgetDashboard/widgetDashboard.enums'
import {useStyles} from './styles'
import {ExtraSmallButton} from '../../../components/custom'
import SecuritySearchShortcutsRegister from '../../../components/KeyboardShortcuts/SecuritySearchShortcuts'
import WatchlistShortcutRegister from '../../../components/KeyboardShortcuts/WatchlistShortcut'
import StrategyBuilderHeader from '../../../components/strategyBuilder/header'
import OptionChainHeader from '../../../components/optionChain/header'
import HorizontalScrollButtons from '../../../components/HorizontalScrollWrapper/horizontalScrollWrapper'
import StockBarActions from './stockBarActions'
import LayoutActions from './layoutActions'
import LoadAsLayoutButton from '../../../components/loadAsLayoutButton/loadAsLayoutButton'
import QuickSearchButton from '../../../components/QuickSearch/QuickSearchButton'
import SettingsOutlinedIcon from '../../../assets/icons/SettingOutlined'
import ChartSettingsDialogs from '../../../components/chartSettings/ChartSettingsDialogs'
import {ChartType} from '../../../proto/generated/chart'
import ScalperLayoutMenu from '../../scalper/scalperLayoutMenu/scalperLayoutMenu'
import ScalperActionBar from '../../scalper/scalperActionBar/scalperActionBar'
import OpenInterestHeader from '../openInterestHeader/OpenInterestHeader'

interface Props {
    viewType: ViewType
    ivPlotTimeBucket: boolean
}

export const scrollButtonStyles: SxProps<Theme> = {
    position: 'absolute',
    height: '100%',
    minWidth: '30px',
    top: 0,
    zIndex: 99,
    color: (theme) => theme.palette.text.primary,
    background: (theme) => alpha(theme.palette.background.paper, 0.8),
    borderRadius: 0,
    padding: 0,
    opacity: 0
}

const StockBar = ({ivPlotTimeBucket, viewType}: Props) => {
    const {classes} = useStyles()
    const appContext = useContext(AppContext)
    const {width, ref} = useResizeDetector({
        handleHeight: false,
        refreshMode: 'debounce',
        refreshRate: 300
    })
    const viewKey = useSelector(selectActiveChartId)
    const chartConfig = useSelector(selectChartConfig(viewKey))
    const activeChartId = useSelector(selectActiveChartId)
    const chartFocused = useSelector(selectChartFocused)
    const activeLayoutId = useSelector(selectActiveLayoutId)
    const activeWidget = useSelector(selectActiveWidget(activeLayoutId))
    const [isSettingsOpen, setIsSettingsOpen] = useState(false)
    const containerRef = useRef<HTMLDivElement>(undefined)
    const handleOpenSettings = () => setIsSettingsOpen(true)
    const handleCloseSettings = () => setIsSettingsOpen(false)
    const selectedChartType = useMemo(() => chartConfig?.chartType ?? ChartType.CANDLESTICK, [chartConfig])
    const replayModeState = useSelector(selectChartReplayMode)

    const disableChartOptions = useMemo(() => {
        if (replayModeState || (activeWidget?.widgetType && [WidgetTypes.MULTI_STRIKE_CHART, WidgetTypes.OI_CHART].includes(activeWidget?.widgetType))) {
            return true
        }
        return !chartFocused
    }, [chartFocused, activeWidget, replayModeState])
    const disableAlertButton = useMemo(() => {
        if (replayModeState || ((activeWidget?.widgetType && [WidgetTypes.MULTI_STRIKE_CHART, WidgetTypes.OI_CHART].includes(activeWidget?.widgetType)) || ivPlotTimeBucket)) {
            return true
        }
        return !activeChartId
    }, [activeChartId, activeWidget?.widgetType, ivPlotTimeBucket, replayModeState])

    const onIndicatorSelected = useCallback(
        (indicator: indicatorProto.Indicator) => {
            if (activeChartId && appContext.chart && appContext.chart[activeChartId]) {
                appContext.chart[activeChartId]?.addIndicator(indicator)
            }
        },
        [activeChartId, appContext]
    )

    SecuritySearchShortcutsRegister()

    const isChartView = useMemo(() => viewType === ViewType.CHART, [viewType])
    const showChartActions = useMemo(() => [ViewType.CHART, ViewType.SYMBOL, ViewType.SPREAD, ViewType.SCALPER].includes(viewType), [viewType])
    const isStandaloneView = useMemo(() => [ViewType.SYMBOL, ViewType.SPREAD].includes(viewType), [viewType])

    return (
        <HorizontalScrollButtons containerRef={containerRef}>
            <Paper
                ref={ref}
                sx={{
                    borderRadius: '4px 4px 0 0',
                    gridArea: 'header',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'nowrap',
                    position: 'relative',
                    minHeight: 40,
                    '&:hover .scroll-buttons': {
                        opacity: '1 !important'
                    }
                }}
                key={`stockbar`}
            >
                <WatchlistShortcutRegister/>
                <Box
                    className={'stock-bar'}
                    ref={containerRef}
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        height: '100%',
                        flex: 1,
                    
                        padding: "2px 4px"
                    }}
                >
                    <Stack direction="row" justifyContent="start" alignItems="center" flex={1} gap={1.5}>
                        <SecuritySearch view={viewType} showIndexDropDown={viewType === ViewType.SIMULATOR ? true : false} isOnlyIndexSecurity={viewType === ViewType.SIMULATOR ? true : false} hideExpression={(viewType === ViewType.SIMULATOR || viewType === ViewType.SCALPER )? true : false} />
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                width: 'fit-content',
                                flex: 1,
                                justifyContent: 'start',
                                height: '100%',
                                gap: 0
                            }}
                        >
                            <Stack gap={2} className={classes.ltpStack}>
                                <LtpView view={viewType} width={width}/>
                                <MarketStatus/>
                            </Stack>
                            {showChartActions && (
                                <StockBarActions
                                    disableChartOptions={disableChartOptions}
                                    onIndicatorSelected={onIndicatorSelected}
                                    disableAlertButton={disableAlertButton}
                                />
                            )}
                            {viewType === ViewType.OPTION_CHAIN && <OptionChainHeader />}
                            {viewType === ViewType.STRATEGY_BUILDER && <StrategyBuilderHeader />}
                            {viewType === ViewType.SIMULATOR && <StrategyBuilderHeader optionChainViewKey={OptionChainViewKey.HISTORICAL} />}
                            {viewType === ViewType.OPEN_INTEREST && <OpenInterestHeader />}
                        </Box>
                    </Stack>
                    {(isChartView || isStandaloneView) && <>
                        <QuickSearchButton disabled={disableChartOptions} />
                        <span>
                        <ExtraSmallButton
                            color="inherit"
                            onClick={handleOpenSettings}
                            disabled={disableChartOptions}
                        ><SettingsOutlinedIcon />
                        </ExtraSmallButton></span>
                        <ChartSettingsDialogs
                            open={isSettingsOpen}
                            onClose={handleCloseSettings}
                            viewKey={viewKey}
                            chartType={selectedChartType}
                        />
                    </>}
                    {isChartView && (
                        <Stack direction="row" alignItems="center" sx={{ height: '100%' }}>
                            <LayoutActions viewType={viewType} />
                        </Stack>
                    )}
                    {isStandaloneView && <LoadAsLayoutButton chartView={viewType.valueOf()}/>}
                    {viewType === ViewType.SCALPER && <ScalperActionBar />}
                    {viewType === ViewType.SCALPER && <ScalperLayoutMenu />}

                </Box>
            </Paper>
        </HorizontalScrollButtons>
    )
}

export default memo(StockBar)
